<template>
  <div>
    <div class="wrapper">
      <breadcrumb :crumbs="[$t('menu.allocates'), $t('text.list')]">
        <el-dropdown
          v-if="Permit.canActionsChargeEdition('mmsInventoryInOutManage')"
          menu-align="start"
          trigger="click"
        >
          <el-button type="primary" size="mini">
            {{ $t("allocateTypes.in") }}
            <i class="el-icon-caret-bottom el-icon--right" />
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="warehouse of warehouses"
              :key="warehouse.id"
            >
              <div
                @click="
                  $router.push('/normal/mms/allocates/new-in/' + warehouse.id)
                "
              >
                {{ warehouse.name }}
              </div>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </breadcrumb>
    </div>
    <narrow :narrows="narrows" @narrowSelect="narrowSelect" />
    <div class="search with-narrows">
      <el-form
        :inline="true"
        @submit.native.prevent="search"
        :model="searchForm"
        size="small"
      >
        <el-form-item>
          <el-input
            size="small"
            v-model.trim="searchForm.serialNo"
            :placeholder="$t('allocate.serialNo')"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            size="small"
            v-model.trim="searchForm.keyword"
            :placeholder="$t('material.serialNoName')"
          />
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-search"
            native-type="submit"
          />
        </el-form-item>
      </el-form>
    </div>
    <div class="wrapper table-area">
      <sticky
        :sticky-top="50"
        :viewport="1024"
        :is-rem="false"
        style="min-height: 52px"
      >
        <vxe-toolbar ref="recordsToolbar" custom zoom class="table-toolbar">
          <template v-slot:buttons>
            <span class="pager-total">
              {{ $t("rules.pagerTotal", [pager.total]) }}
            </span>
          </template>
        </vxe-toolbar>
      </sticky>

      <vxe-table
        :data="records"
        :loading="loading"
        id="stockInsTable"
        row-id="id"
        ref="recordsTable"
        class="recordsTable"
        size="mini"
        resizable
        round
        auto-resize
        highlight-hover-row
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column
          field="serialNo"
          :title="$t('allocate.serialNo')"
          min-width="130"
        >
          <template v-slot="{ row }">
            {{ row.serialNo }}
            <div :class="row.type == 'in' ? 'green' : 'orange'" class="font10">
              {{ $t("allocateTypes." + row.type) }}
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="materialSerialNo"
          :title="$t('material.serialNo')"
          min-width="130"
        >
          <template v-slot="{ row }">
            {{ row.materialSerialNo }}
            <div class="font10">
              {{ row.materialName }} {{ row.materialSubjectName }}
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="materialColor"
          :title="$t('material.color')"
          min-width="100"
        >
          <template v-slot="{ row }">
            {{ row.materialColor }}
            <div class="font10">
              {{ row.materialColorNo }}
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="quantity"
          :title="$t('text.quantity')"
          min-width="70"
        >
          <template v-slot="{ row }">
            {{ row.quantity }}{{ row.materialUnit }}
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="workflowState"
          :title="$t('text.workflowState')"
          min-width="80"
        >
          <template v-slot="{ row }">
            <state-label :state="row.workflowState">
              {{ $t("allocateWorkflowStateMms." + row.workflowState) }}
            </state-label>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="applyDepartName"
          :title="$t('allocate.applyDepartInfo')"
          min-width="160"
        >
          <template v-slot="{ row }">
            {{ row.applyWarehouseName }}
            <div class="font10">{{ row.applyName }} 于 {{ row.applyAt }}</div>
            <raw-html-limit-number :content="row.applyRemark" :number="20" />
          </template>
        </vxe-table-column>

        <vxe-table-column
          field="acceptDepartName"
          :title="$t('allocate.acceptDepartInfo')"
          min-width="160"
        >
          <template v-slot="{ row }">
            {{ row.acceptWarehouseName }}
            <span v-if="row.workflowState == 'rejected'">
              <div class="font10">
                {{ row.rejecterName }} 于 {{ row.rejectAt }}
              </div>
              <div class="font10">
                <raw-html-limit-number
                  :content="row.rejectReason"
                  :number="20"
                />
              </div>
            </span>
            <span v-else>
              <div class="font10">{{ row.accepterName }}{{ row.acceptAt }}</div>
              <raw-html-limit-number :content="row.acceptRemark" :number="20" />
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="action"
          :title="$t('text.action')"
          width="90"
          fixed="right"
        >
          <template v-slot="{ row }">
            <el-button
              v-if="
                row.workflowState == 'applying' &&
                mmsAuthWarehousesIds.includes(row.applyWarehouseId)
              "
              type="text"
              size="small"
              @click="edit(row)"
            >
              {{ $t("text.edit") }}
            </el-button>
            <el-popconfirm
              v-if="
                row.workflowState == 'applying' &&
                mmsAuthWarehousesIds.includes(row.applyWarehouseId)
              "
              :title="$t('prompt.confirmDelete')"
              @confirm="deleteRow(row)"
              class="mgl10"
            >
              <el-button slot="reference" type="text" size="mini">
                {{ $t("text.delete") }}
              </el-button>
            </el-popconfirm>
            <div>
              <el-button
                v-if="
                  row.workflowState == 'applying' &&
                  row.type == 'in' &&
                  mmsAuthWarehousesIds.includes(row.acceptWarehouseId)
                "
                type="text"
                size="small"
                @click="stockOut(row)"
                >{{ $t("action.accept") }}</el-button
              >
              <el-button
                v-if="
                  row.workflowState == 'applying' &&
                  row.type == 'in' &&
                  mmsAuthWarehousesIds.includes(row.acceptWarehouseId)
                "
                type="text"
                size="small"
                @click="reject(row)"
              >
                {{ $t("action.reject") }}
              </el-button>
            </div>
            <el-button
              v-if="
                row.workflowState == 'outed' &&
                mmsAuthWarehousesIds.includes(row.applyWarehouseId)
              "
              type="text"
              size="small"
              @click="stockIn(row)"
              >{{ $t("action.stockIn") }}</el-button
            >
          </template>
        </vxe-table-column>
      </vxe-table>
      <div class="flex flex-between pager">
        <div class="selected-qty-tips">
          选中了
          <span class="strong">{{ selectRecordQty }}</span>
          条记录
        </div>
        <div>
          <vxe-pager
            size="small"
            :loading="false"
            :background="false"
            :current-page="pager.currentPage"
            :page-size="pager.pageSize"
            :total="pager.total"
            :layouts="pagerConfig.layouts"
            :page-sizes="pagerConfig.pageSizes"
            @page-change="changePager"
          ></vxe-pager>
        </div>
      </div>
    </div>

    <!-- 编辑 -->
    <el-dialog
      :title="$t('text.edit')"
      :close-on-click-modal="false"
      :visible.sync="editDF"
      width="30%"
    >
      <el-form ref="editObjForm" :model="editObj" label-width="70px">
        <el-form-item
          :label="$t('text.quantity')"
          :prop="'quantity'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('text.quantity')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            size="small"
            v-model.trim="editObj.quantity"
            @blur="blurAllocateQuantity"
          />
        </el-form-item>

        <el-form-item
          :label="$t('allocate.acceptDepartName')"
          :prop="'acceptWarehouseId'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('allocate.acceptDepartName')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-select
            size="small"
            v-model="editObj.acceptWarehouseId"
            @change="blurAllocateQuantity"
          >
            <el-option
              v-for="invWarehouse in invWarehouses"
              :key="invWarehouse.warehouseId"
              :label="invWarehouse.text"
              :value="invWarehouse.warehouseId"
            />
          </el-select>
        </el-form-item>
        <el-form-item :prop="'applyRemark'" :label="$t('allocate.applyRemark')">
          <el-input
            size="small"
            v-model.trim="editObj.applyRemark"
            type="textarea"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="editDoing"
          size="small"
          @click="editSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="editDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
    <!-- 拒绝 -->
    <el-dialog
      :title="$t('action.reject')"
      :close-on-click-modal="false"
      :visible.sync="rejectDF"
      width="30%"
    >
      <el-form ref="rejectForm" :model="rejectObj">
        <el-form-item :prop="'rejectReason'">
          <el-input
            size="small"
            v-model.trim="rejectObj.rejectReason"
            type="textarea"
            :placeholder="$t('allocate.rejectReason')"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          :loading="rejectDoing"
          size="small"
          @click="rejectSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="rejectDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import Vue from "vue";
import axios from "axios";
import { page, table } from "@/utils/mixins.js";
import Util from "@/utils/index.js";
import RawHtmlLimitNumber from "@/components/RawHtmlLimitNumber";
import StateLabel from "@/components/StateLabel";

import Warehouse from "@/api/warehouse.js";
import UserWarehouse from "@/api/userWarehouse.js";
import AllocateMms from "@/api/allocateMms.js";
import InventoryMms from "@/api/inventoryMms.js";

export default {
  mixins: [
    page,
    table.pager,
    table.narrow,
    table.search,
    table.checker,
    table.sorter,
    table.basic,
  ],
  components: {
    RawHtmlLimitNumber,
    StateLabel,
  },
  data() {
    return {
      Util: Util,
      narrows: [
        [
          {
            label: this.$t("text.workflowState"),
            items: AllocateMms.states.map((t) => {
              return {
                key: t,
                label: this.$t("allocateWorkflowStateMms." + t),
                condition: { workflowStates: [t] },
              };
            }),
          },
          {
            label: this.$t("text.type"),
            items: [
              {
                label: this.$t("allocateTypes.in"),
                key: "in",
                condition: { type: "in" },
              },
              {
                label: this.$t("allocateTypes.out"),
                key: "out",
                condition: { type: "out" },
              },
            ],
          },
        ],
      ],

      searchForm: {
        serialNo: null,
        keyword: null,
      },
      sorter: {
        sortBy: "allocates_id",
        sortIndicator: "desc",
      },
      warehouses: [],
      userWarehouses: [],
      mmsAuthWarehouses: [],
      mmsAuthWarehousesIds: [],
      // 编辑
      row: null,
      editDF: false,
      invWarehouses: [],
      editObj: {
        quantity: null,
        applyRemark: null,
        acceptWarehouseId: null,
        acceptWarehouseName: null,
        _maxAllocateQuantity: null,
        _type: null,
      },
      editDoing: false,
      // 拒绝
      rejectDF: false,
      rejectObj: {
        rejectReason: null,
      },
      rejectDoing: false,
    };
  },

  created() {
    const self = this;
    this.$nextTick(() => {
      // 手动将表格和工具栏进行关联
      this.$refs.recordsTable.connect(this.$refs.recordsToolbar);
    });
    self.loadingStart();
    axios
      .all([
        Warehouse.findByTenantId(),
        UserWarehouse.findByUserId(self.Permit.currentUser().id),
      ])
      .then(
        axios.spread(function (warehouses, userWarehouses) {
          self.warehouses = warehouses.data;
          self.userWarehouses = userWarehouses.data;
          if (self.Permit.currentUser().admin) {
            self.mmsAuthWarehouses = self.warehouses;
          } else {
            const userWarehouseIds = self.userWarehouses.map(
              (u) => u.warehouseId
            );
            self.mmsAuthWarehouses = self.warehouses.filter((w) =>
              userWarehouseIds.includes(w.id)
            );
          }
          self.mmsAuthWarehousesIds = self.mmsAuthWarehouses.map((m) => m.id);
          self.loadingEnd();
        })
      );
  },
  methods: {
    // 入库
    stockIn(row) {
      const self = this;
      AllocateMms.stockIn(row.id).then(function (res) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.stockInSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },
    //  拒绝
    reject(row, rowIndex) {
      this.row = row;
      this.rowIndex = rowIndex;
      this.rejectObj.rejectReason = null;
      this.rejectDoing = false;
      this.rejectDF = true;
    },
    rejectSubmit() {
      const self = this;
      self.rejectDoing = true;
      AllocateMms.reject(self.row.id, self.rejectObj).then(function (res) {
        if (res.status === 200) {
          self.rejectDoing = false;
          self.rejectDF = false;
          self.$message({
            message: self.$t("prompt.rejectSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },

    // 出库
    stockOut(row) {
      const self = this;
      InventoryMms.findBySerialNoWithWarehouse({
        serialNo: row.materialSerialNo,
        warehouseId: row.acceptWarehouseId,
      }).then(function (res) {
        if (res.data.code === 200) {
          const invObj = res.data.entity;
          if (row.quantity > invObj.storeQuantity) {
            self.$message({
              message: self.$t("prompt.invQtyNotEnough"),
              type: "warning",
            });
          } else {
            AllocateMms.acceptStockOut(row.id, { acceptRemark: "" }).then(
              function (res) {
                if (res.status === 200) {
                  self.$message({
                    message: self.$t("prompt.stockOutSuccess"),
                    type: "success",
                  });
                  self.loadData();
                } else {
                  self.sysError();
                }
              }
            );
          }
        } else {
          self.$message({
            message: self.$t("prompt.invNotExist"),
            type: "danger",
          });
        }
      });
    },

    // 删除
    deleteRow(row) {
      const self = this;
      AllocateMms.delete(row.id).then(function (res) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.delSuccess"),
            type: "success",
          });
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },

    // 编辑
    edit(row) {
      const self = this;
      this.row = row;
      this.editObj.quantity = row.quantity;
      this.editObj.applyRemark = row.applyRemark;
      self.editObj._type = row.type;
      if (row.type === "in") {
        InventoryMms.findBySerialNos({
          serialNos: [row.materialSerialNo],
        }).then(function (res) {
          if (res.status == 200) {
            const invWarehouses = res.data;
            invWarehouses.forEach(
              (ii) =>
                (ii.text =
                  ii.warehouseName +
                  "(" +
                  ii.storeQuantity +
                  ii.material.unit +
                  ")")
            );
            self.invWarehouses = invWarehouses.filter(
              (ii) => ii.warehouseId !== self.row.applyWarehouseId
            );
            const obj = self.invWarehouses.find(
              (w) => w.warehouseId == self.row.acceptWarehouseId
            );
            if (obj != null) {
              self.editObj.acceptWarehouseId = self.row.acceptWarehouseId;
              self.blurAllocateQuantity();
            }
          } else {
            self.sysError();
          }
        });
      }
      this.editDF = true;
    },
    blurAllocateQuantity() {
      const self = this;
      if (
        this.editObj.acceptWarehouseId != null &&
        this.editObj.quantity != null
      ) {
        if (self.editObj._type === "in") {
          const selectedInv = self.invWarehouses.find(
            (is) => is.warehouseId === self.editObj.acceptWarehouseId
          );
          self.editObj._maxAllocateQuantity = selectedInv.storeQuantity;
          self.editObj.acceptWarehouseName = selectedInv.warehouseName;
          if (self.editObj.quantity > self.editObj._maxAllocateQuantity) {
            this.$message({
              message: self.$t("rules.beyondMaxQty", [
                self.allocateObj._maxAllocateQuantity + self.row.materialUnit,
              ]),
              type: "warning",
            });
            self.editObj.quantity = null;
          }
        }
      }
    },
    editSubmit() {
      const self = this;
      self.$refs.editObjForm.validate((valid) => {
        if (valid) {
          if (!Util.regexQuantity(this.editObj.quantity)) {
            this.$message({
              message: self.$t("rules.regex", [self.$t("text.quantity")]),
              type: "warning",
            });
            return;
          }
          if (this.editObj.quantity > self.editObj._maxAllocateQuantity) {
            this.$message({
              message: self.$t("rules.beyondMaxQty", [
                self.editObj._maxAllocateQuantity + self.row.materialUnit,
              ]),
              type: "warning",
            });
            return;
          }
          self.editDoing = true;
          AllocateMms.update(self.row.id, self.editObj).then(function (res) {
            self.editDoing = false;
            self.editDF = false;
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.editSuccess"),
                type: "success",
              });
              self.loadData();
            } else {
              self.sysError();
            }
          });
        } else {
          return false;
        }
      });
    },
    //-----
    dataProvider(params) {
      return AllocateMms.search(params);
    },
  },
};
</script>
